import { memo } from 'react';
import { Grid, Divider, useEventCallback } from '@mui/material';
import { PmiTitleDivider } from 'shell/components/shared/pmi-title-divider';
import { ForeignVM } from 'practice/view-models/pratice-actual-residence';
import { FormikErrors, FormikTouched } from 'formik';
import ForeignStateList, { ForeignStateVM } from 'shell/components/shared/foreign-state-list';
import MunicipalityList, {
  MunicipalityAnprVM,
} from 'shell/components/shared/foreign-municipality-list';
import PmiTextField from 'shell/components/shared/text-field';
import { noop } from 'shell/utils/noop';

interface FormProps {
  values?: ForeignVM;
  onChange: (key: keyof ForeignVM, value: ForeignVM[keyof ForeignVM]) => void;
  errors?: FormikErrors<ForeignVM>;
  touched?: FormikTouched<ForeignVM>;
  disabled?: boolean;
  ForPracticeTemporary?: boolean;
}

const ForeignFormComponent = ({
  values,
  onChange,
  errors,
  touched,
  disabled,
}: FormProps): React.ReactElement => {
  const onChangeForeignState = useEventCallback((state: ForeignStateVM | null | undefined) => {
    onChange('foreignState', state);
  });

  const onChangeForeignMunicipality = useEventCallback(
    (municipality: MunicipalityAnprVM | null | undefined) => {
      onChange('municipality', municipality);
    }
  );

  return (
    <Grid item container spacing={2}>
      <Grid item xs={12}>
        <PmiTitleDivider>Località estera</PmiTitleDivider>
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid item xs={12} container spacing={2}>
        <Grid item xs={12} md={6}>
          <ForeignStateList
            label="Stato estero"
            value={values?.foreignState || null}
            onChange={onChangeForeignState}
            id="foreignState"
            disabled={disabled}
            error={touched?.foreignState && Boolean(errors?.foreignState)}
          />
        </Grid>
      </Grid>
      <Grid item xs={12} md={6}>
        <MunicipalityList
          value={values?.municipality}
          disabled={!values?.foreignState?.istatCode || disabled}
          istatCodeState={values?.foreignState?.istatCode}
          onChange={onChangeForeignMunicipality}
          label="Comune estero"
          id="foreignMunicipality"
          error={touched?.municipality && Boolean(errors?.municipality)}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <PmiTextField
          label="Provincia/Contea"
          aria-label="Indirizzo"
          id="indirizzo"
          value={values?.municipality?.province || ''}
          onChange={noop}
          disabled
          fullWidth
        />
      </Grid>
      <Grid item xs={12} container spacing={2}>
        <Grid item xs={12} md={4}>
          <PmiTextField
            label="CAP"
            aria-label="CAP"
            id="cap"
            disabled={disabled}
            value={values?.cap || ''}
            onChange={(event) => onChange('cap', event.target.value)}
            fullWidth
          />
        </Grid>
      </Grid>
      <Grid item xs={12} md={8}>
        <PmiTextField
          label="Indirizzo"
          aria-label="Indirizzo"
          id="address"
          disabled={disabled}
          value={values?.address || ''}
          onChange={(event) => onChange('address', event.target.value)}
          fullWidth
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <PmiTextField
          label="Nr. civico"
          aria-label="numero civico"
          id="civicNumber"
          value={values?.civicNumber || ''}
          onChange={(event) => onChange('civicNumber', event.target.value)}
          fullWidth
          type="number"
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <PmiTextField
          aria-label="Consolato"
          label="Consolato"
          id="consulateCode"
          name="consulateCode"
          fullWidth
          type="text"
          variant="outlined"
          disabled
          value={values?.consulateCode?.value || ''}
          onChange={noop}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <PmiTextField
          aria-label="Territorio"
          label="Territorio"
          id="territory"
          name="territory"
          fullWidth
          type="text"
          variant="outlined"
          disabled
          value={values?.territory?.value || ''}
          onChange={noop}
        />
      </Grid>
    </Grid>
  );
};

ForeignFormComponent.displayName = 'Accordion New Residence Form - Foreign Form';

const ForeignForm = memo(ForeignFormComponent);

export default ForeignForm;
